
import { Injectable } from '@angular/core';
import { filter, first, map, switchMap, tap, take, auditTime, distinctUntilChanged, delay, timeout, catchError } from 'rxjs/operators';
import { ServerResponse } from '@app/enums/server-response';
import { select, Store } from '@ngrx/store';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { LobbyService } from '../../../features/lobby/services/lobby.service';

import * as LobbyActions from '../lobby/lobby.actions';
import * as SpinNGoSummariesActions from './spin-n-go-summaries.actions';
import * as SpinNGoSummariesSelector from './spin-n-go-summaries.selector';

import { WsService } from '@app/services/ws.service';
import { SpinNGoSummary, SpinNGoSummaryResponse } from '@app/models/spin-n-go-summary';
import { LiveLobbyTournament } from '@app/models/live-lobby-tournament';
import { isEqual } from 'lodash';
import * as UserSelectors from '@app/store/features/user/user.selectors';
import { combineLatest, forkJoin, of, EMPTY } from 'rxjs';
import { TournamentInformationResponse } from '@app/models/tournament-information';

@Injectable()
export class SpinNGoSummariesEffects {
    constructor(
        private _ws: WsService,
        private readonly store: Store,
        private readonly lobbyService: LobbyService,
        private actions$: Actions,

    ) { }

    // updateActiveNav$ = createEffect(
    //     () =>
    //         this.actions$.pipe(
    //             ofType(LobbyActions.updateActiveNav),
    //             filter(({ activeNav }) => activeNav === LobbyNav.SpinNGo),
    //             tap(() => this.lobbyService.getSpinNGoSummaries()),
    //             map(() => SpinNGoSummariesActions.updateLoader({ loader: true }))
    //         ));


    updateSpinNGoSummaries$ = createEffect(() =>
        this._ws.getDataResponse<SpinNGoSummaryResponse[]>(ServerResponse.SpinNGoSummaries)
            .pipe(
                map(spinNGoSummariesResponse => {
                    const spinNGoSummaries: SpinNGoSummary[] = spinNGoSummariesResponse.map(spinNGoSummaryResponse => {
                        return this.lobbyService.updateSpinNGoSummary(spinNGoSummaryResponse)
                    })

                    return SpinNGoSummariesActions.updateAll({ spinNGoSummaries })
                })
            ));


    liveLobbyTournament$ = createEffect(() =>
        this._ws.getDataResponse<LiveLobbyTournament>(ServerResponse.LiveLobbyTournament)
            .pipe(
                // 🔧 FIX: Use switchMap to cancel previous inner subscriptions
                switchMap(liveLobbyTournament =>
                    this.store.pipe(
                        select(SpinNGoSummariesSelector.selectEntityById(liveLobbyTournament.idTournament)),
                        filter(tourSum => !!tourSum),
                        take(1), // 🔧 FIX: Complete inner subscription after first valid value
                        timeout(5000), // 🔧 FIX: Prevent hanging if entity doesn't exist
                        map(spinNGoSummary => ({ liveLobbyTournament, spinNGoSummary })),
                        catchError(() => EMPTY) // 🔧 FIX: Skip if entity not found
                    )
                ),
                map(({ liveLobbyTournament, spinNGoSummary }) => {
                    // Immutable update - no mutation
                    const data: SpinNGoSummary = {
                        ...spinNGoSummary!,
                        status: liveLobbyTournament.status,
                        status2: liveLobbyTournament.status2,
                        nbPlayerRegistered: liveLobbyTournament.nbPlayersRegistered,
                        nbPlayersActive: liveLobbyTournament.nbPlayersActive,
                        startIn: liveLobbyTournament.startIn
                    };

                    return SpinNGoSummariesActions.updateOne({ spinNGoSummary: data })
                }),
                catchError((error) => {
                    console.warn('SpinNGo liveLobbyTournament$ error:', error);
                    return EMPTY;
                })
            )
    );


    updateTournamentInformationResponse$ = createEffect(() =>
        this._ws.getDataResponse<TournamentInformationResponse>(ServerResponse.TournamentInformation)
            .pipe(
                delay(200), //wait for tournament info to be processed
                auditTime(120), //collapse bursts: keep only the last event every 120ms
                distinctUntilChanged((a, b) => isEqual(a, b)),

                // 🔧 FIX: Use switchMap to cancel previous inner subscriptions
                switchMap((tournamentInformationResponse) => {
                    return combineLatest([
                        of(tournamentInformationResponse),
                        this.store.pipe(select(UserSelectors.selectUserProfile), take(1)),
                        this.store.pipe(
                            select(SpinNGoSummariesSelector.selectEntityById(tournamentInformationResponse.idTournament)),
                            filter(data => !!data),
                            map(data => data!),
                            take(1), // 🔧 FIX: Complete inner subscription
                            timeout(5000), // 🔧 FIX: Prevent hanging
                            catchError(() => EMPTY) // 🔧 FIX: Handle missing entity
                        )
                    ])
                }),
                filter(([_, __, spinNGoSummary]) => !!spinNGoSummary), // 🔧 FIX: Skip if not found
                map(([tournamentInformationResponse, userProfile, spinNGoSummary]) => {
                    const myPlayer = tournamentInformationResponse.players.find(player => player.idPlayer === userProfile?.id);
                    const imOnTour = !myPlayer?.positionEnded ? myPlayer : undefined;
                    const iWasOnTour = myPlayer?.positionEnded ? myPlayer : undefined;
                    const isRegistered = !!(imOnTour && !imOnTour.positionEnded);

                    // Immutable update - no mutation
                    const tourSumGame = {
                        ...spinNGoSummary.game,
                        imOnTour: !!imOnTour,
                        iWasOnTour: !!iWasOnTour,
                        isRegistered: isRegistered
                    };

                    return SpinNGoSummariesActions.updateOne({
                        spinNGoSummary: {
                            ...spinNGoSummary,
                            nbPlayerRegistered: tournamentInformationResponse.nbPlayerRegistered,
                            nbRebuyUsed: tournamentInformationResponse.nbRebuyUsed,
                            nbAddonUsed: tournamentInformationResponse.nbAddonUsed,
                            imRegistered: isRegistered,
                            game: tourSumGame
                        }
                    })
                }),
                catchError((error) => {
                    console.warn('SpinNGo updateTournamentInformationResponse$ error:', error);
                    return EMPTY;
                })
            )
    );

} 